import React from 'react';
import { Box, Container, Flex, HStack, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FitHeadline, HeroRecede, useMotionAllowed, EASE } from '../motion';
import { clearGlass } from '../glass';

/**
 * The hero for every inner page, built the same way as the home hero.
 *
 * Every inner page used to carry an identical cyan-to-blue gradient with the
 * relevant facility photograph crushed to ten percent opacity underneath it.
 * The photograph was always there; it was just invisible. This shows it, under
 * the same deep teal-ink scrim the home hero uses, so each page opens on the
 * actual place it is about rather than on a flat brand color.
 *
 * Shape matches the home hero too: a rounded card inset from the page edges,
 * starting at the top so the header tab drops down into it on desktop.
 *
 * `title` may be a string or an array of lines. Headlines never wrap (the
 * viewport would choose the break), so a long string is split once at the word
 * boundary nearest its middle. Only the line break is chosen here; the words
 * are passed through untouched.
 */

const SCRIM =
  'radial-gradient(ellipse 72% 64% at 50% 52%, rgba(14,47,58,0.56) 0%, rgba(14,47,58,0.24) 72%), ' +
  'linear-gradient(180deg, rgba(14,47,58,0.42) 0%, rgba(14,47,58,0.46) 55%, rgba(14,47,58,0.82) 100%)';

const toLines = (title) => {
  if (Array.isArray(title)) return title;
  const text = String(title);
  if (text.length <= 22) return [text];
  const words = text.split(' ');
  if (words.length < 2) return [text];
  const mid = text.length / 2;
  let best = 1;
  let bestDist = Infinity;
  let pos = 0;
  for (let i = 0; i < words.length - 1; i += 1) {
    pos += words[i].length + 1;
    const dist = Math.abs(pos - mid);
    if (dist < bestDist) {
      bestDist = dist;
      best = i + 1;
    }
  }
  return [words.slice(0, best).join(' '), words.slice(best).join(' ')];
};

const PageHero = ({
  title,
  subtitle,
  eyebrow,
  image,
  imagePosition = 'center',
  children,
  minH = { base: '440px', md: '520px', lg: '600px' },
}) => {
  const motionAllowed = useMotionAllowed();

  // Subtitle and extras follow the headline lines out of their masks. The
  // target is always the final, fully visible state; only the starting point
  // and timing depend on the motion setting. If that setting flips mid-flight
  // the element still finishes visible rather than being stranded part way.
  const follow = (delay) => ({
    initial: motionAllowed ? { opacity: 0, y: 16 } : false,
    animate: { opacity: 1, y: 0 },
    transition: motionAllowed ? { duration: 0.9, ease: EASE, delay } : { duration: 0 },
  });

  return (
    <Box pt={{ base: '76px', nav: 0 }}>
      <Box
        as="section"
        position="relative"
        minH={minH}
        overflow="hidden"
        borderRadius={{ base: '20px', md: '28px' }}
        w="96vw"
        ml="calc(50% - 48vw)"
        bg="brand.900"
      >
        {image && (
          <HeroRecede
            position="absolute"
            inset={0}
            bgImage={`url('${image}')`}
            bgSize="cover"
            bgPosition={imagePosition}
            bgRepeat="no-repeat"
            _after={{
              content: '""',
              position: 'absolute',
              inset: 0,
              bg: SCRIM,
            }}
          />
        )}

        <Container maxW="container.xl" position="relative" zIndex={1}>
          <Flex
            minH={minH}
            direction="column"
            align="center"
            justify="center"
            textAlign="center"
            color="white"
            px={{ base: 2, md: 0 }}
            // Clears the header tab, which drops into the hero on desktop.
            pt={{ base: 10, nav: '146px' }}
            pb={{ base: 12, md: 16 }}
          >
            {eyebrow && (
              <motion.div {...follow(0.05)}>
                <HStack
                  display="inline-flex"
                  spacing={2.5}
                  px={4}
                  py={2}
                  mb={6}
                  borderRadius="pill"
                  sx={clearGlass}
                >
                  <Box w="6px" h="6px" borderRadius="full" bg="starOfLife.400" />
                  <Text
                    fontSize={{ base: 'xs', md: 'sm' }}
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="0.18em"
                  >
                    {eyebrow}
                  </Text>
                </HStack>
              </motion.div>
            )}

            <Box w="100%" maxW="900px">
              <FitHeadline
                as="h1"
                lines={toLines(title)}
                size={{ base: '2xl', md: '3xl' }}
                color="white"
                fontWeight="600"
                lineHeight="1.08"
                letterSpacing="-0.025em"
                reveal
                revealDelay={0.15}
              />
            </Box>

            {subtitle && (
              <motion.div {...follow(0.5)}>
                <Text
                  fontSize={{ base: 'lg', md: 'xl' }}
                  lineHeight="1.7"
                  color="whiteAlpha.900"
                  maxW="3xl"
                  mx="auto"
                  mt={6}
                >
                  {subtitle}
                </Text>
              </motion.div>
            )}

            {children && <motion.div {...follow(0.65)}>{children}</motion.div>}
          </Flex>
        </Container>
      </Box>
    </Box>
  );
};

export default PageHero;
